import { defaultHttpClient } from '../../httpClient';
import { FilterType } from './enums';
import type {
  GetScreensFilterOptionsRequest,
  GetScreensFilterOptionsResponse,
  GetScreensFilterOptionsReturnType,
  IImageToggleChildGroup,
  ISlideChildGroup,
} from './types';

/**
 * - 아토믹의 FilterList에서 타입이 select인 필터를 선택시 열리는 바텀시트에 대한 정보를 가져오기 위한 api
 */
export const getScreensFilterOptions = async ({
  category_sno,
  filter_token,
  ...rest
}: GetScreensFilterOptionsRequest): Promise<GetScreensFilterOptionsReturnType> => {
  const { data } =
    await defaultHttpClient.request<GetScreensFilterOptionsResponse>(
      {
        method: 'GET',
        url: `/api/v2/screens/filter_options/`,
        params: {
          filter_token,
          ...(category_sno && {
            category_sno,
          }),
          ...rest,
        },
      },
      {
        arrayFormat: 'repeat',
      },
    );
  return {
    previousScreen: data.previous_screen,
    logging: data.logging,
    totalCount: data.total_count,
    focusingParentGroupIndex: data.focusing_parent_group_index,
    parentGroups: data.parent_groups.map(({ child_groups }) => {
      const groups = child_groups.map(childGroup => {
        if (childGroup.type === FilterType.Single) {
          return {
            title: childGroup.title,
            type: childGroup.type,
            entity: {
              queryName: childGroup.entity.query_name,
              selectedQueryValue: childGroup.entity.selected_query_value,
              possibleItems: childGroup.entity.possible_items.map(
                ({ text, query_value }) => {
                  return { text, queryValue: query_value };
                },
              ),
            },
          };
        }

        if (
          childGroup.type === FilterType.MultiImage ||
          childGroup.type === FilterType.MultiFlow
        ) {
          return {
            title: childGroup.title,
            type: childGroup.type,
            entity: {
              queryName: childGroup.entity.query_name,
              previewItemCount: childGroup.entity.preview_item_count,
              possibleItems: childGroup.entity.possible_items.map(
                possibleItem => {
                  return {
                    text: possibleItem.text,
                    queryValue: possibleItem.query_value,
                    countText: possibleItem.count_text,
                    isSelected: possibleItem.is_selected,
                    ...(possibleItem.image && { image: possibleItem.image }),
                  };
                },
              ),
            },
          };
        }

        if (childGroup.type === FilterType.SingleTree) {
          return {
            title: childGroup.title,
            type: childGroup.type,
            entity: {
              queryName: childGroup.entity.query_name,
              selectedQueryValue: childGroup.entity.selected_query_value,
              possibleItems: childGroup.entity.possible_items.map(
                ({
                  query_value,
                  parent_query_value,
                  count_text,
                  is_selected,
                  text,
                  child_list,
                }) => {
                  return {
                    queryValue: query_value,
                    parentQueryValue: parent_query_value,
                    countText: count_text,
                    isSelected: is_selected,
                    text,
                    childList: child_list.map(
                      ({
                        query_value,
                        parent_query_value,
                        count_text,
                        is_selected,
                        text,
                      }) => {
                        return {
                          queryValue: query_value,
                          parentQueryValue: parent_query_value,
                          countText: count_text,
                          isSelected: is_selected,
                          text,
                        };
                      },
                    ),
                  };
                },
              ),
            },
          };
        }

        if (childGroup.type === FilterType.ImageToggle) {
          const imageToggleGroup = childGroup as IImageToggleChildGroup;
          return {
            title: imageToggleGroup.title,
            type: imageToggleGroup.type,
            entity: {
              possibleItems: imageToggleGroup.entity.possible_items.map(
                item => ({
                  queryName: item.query_name,
                  queryValue: item.query_value,
                  imageRatio: item.image_ratio,
                  image: item.image,
                  isSelected: item.is_selected,
                }),
              ),
            },
          };
        }

        const slideGroup = childGroup as ISlideChildGroup;

        return {
          title: slideGroup.title,
          type: slideGroup.type,
          entity: {
            unitText: slideGroup.entity.unit_text,
            minQueryName: slideGroup.entity.min_query_name,
            maxQueryName: slideGroup.entity.max_query_name,
            rawMaxValue: slideGroup.entity.raw_max_value,
            rawMinValue: slideGroup.entity.raw_min_value,
            selectedMin: slideGroup.entity.selected_min,
            selectedMax: slideGroup.entity.selected_max,
            interval: slideGroup.entity.interval,
            min: slideGroup.entity.min,
            max: slideGroup.entity.max,
            countList: slideGroup.entity.count_list,
          },
        };
      });

      return { childGroups: groups };
    }),
  };
};
